import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/crm/projects/")({
  ssr: false,
  head: () => ({ meta: [{ title: "Projekte" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ProjectsPage,
});

function ProjectsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", customer_id: "", building_type: "", postal_code: "", city: "" });
  const [err, setErr] = useState<string | null>(null);

  const { data: customers } = useQuery({
    queryKey: ["customers-min"],
    queryFn: async () => {
      const { data } = await supabase.from("customers").select("id, customer_no, first_name, last_name, company").order("created_at", { ascending: false }).limit(500);
      return data ?? [];
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, project_no, name, status, city, created_at, customers(customer_no, first_name, last_name, company)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!form.customer_id) throw new Error("Bitte Kunde auswählen.");
      if (!form.name) throw new Error("Bitte Projektnamen angeben.");
      const { data: u } = await supabase.auth.getUser();
      const { data: prof } = await supabase.from("profiles").select("tenant_id").eq("id", u.user!.id).single();
      const { error } = await supabase.from("projects").insert({
        tenant_id: prof!.tenant_id, owner_user_id: u.user!.id, ...form,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      setForm({ name: "", customer_id: "", building_type: "", postal_code: "", city: "" });
      setErr(null);
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["crm-counts"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  function onSubmit(e: FormEvent) { e.preventDefault(); createMut.mutate(); }

  return (
    <PortalShell nav={NAV}>
      <h1 className="text-2xl font-semibold mb-6">Projekte</h1>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Neues Projekt anlegen</CardTitle></CardHeader>
        <CardContent>
          {err && <Alert variant="destructive" className="mb-4"><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label>Projektname</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Kunde</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger><SelectValue placeholder="Kunde wählen …" /></SelectTrigger>
                <SelectContent>
                  {(customers ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.customer_no} – {c.company || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Gebäudetyp</Label><Input placeholder="EFH, MFH, Gewerbe …" value={form.building_type} onChange={(e) => setForm({ ...form, building_type: e.target.value })} /></div>
            <div className="space-y-1"><Label>PLZ</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
            <div className="space-y-1"><Label>Ort</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="sm:col-span-3"><Button type="submit" disabled={createMut.isPending}>{createMut.isPending ? "…" : "Projekt anlegen"}</Button></div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Alle Projekte</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nr.</TableHead><TableHead>Name</TableHead><TableHead>Kunde</TableHead><TableHead>Ort</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(projects ?? []).map((p) => {
                const c = (p as unknown as { customers: { customer_no: string; first_name: string | null; last_name: string | null; company: string | null } | null }).customers;
                const cname = c ? (c.company || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim()) : "—";
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/app/crm/projects/$id" params={{ id: p.project_no }} className="underline">{p.project_no}</Link>
                    </TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{cname}</TableCell>
                    <TableCell>{p.city ?? "—"}</TableCell>
                    <TableCell>{p.status}</TableCell>
                  </TableRow>
                );
              })}
              {(projects ?? []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Noch keine Projekte.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PortalShell>
  );
}